import React, { createContext, useContext, useEffect, useState } from 'react';

// Theme options
const themes = {
  light: {
    name: 'light',
    colors: {
      primary: 'blue',
      background: 'white',
      text: 'gray-900'
    }
  },
  dark: {
    name: 'dark',
    colors: {
      primary: 'blue',
      background: 'gray-900',
      text: 'gray-100'
    }
  }
};

// Create context
const ThemeContext = createContext();

// Theme Provider Component
export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('light');
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Get stored theme or system preference
  useEffect(() => {
    const storedTheme = localStorage.getItem('kslnk_theme');
    
    if (storedTheme) {
      setTheme(storedTheme);
      setIsDarkMode(storedTheme === 'dark');
    } else {
      // Check system preference
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const defaultTheme = systemPrefersDark ? 'dark' : 'light';
      setTheme(defaultTheme);
      setIsDarkMode(defaultTheme === 'dark');
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Store theme preference
    localStorage.setItem('kslnk_theme', theme);
  }, [theme, isDarkMode]);

  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setIsDarkMode(newTheme === 'dark');
  };

  // Set specific theme
  const setSpecificTheme = (themeName) => {
    if (themes[themeName]) {
      setTheme(themeName);
      setIsDarkMode(themeName === 'dark');
    }
  };

  // Get current theme object
  const currentTheme = themes[theme] || themes.light;

  // Context value
  const value = {
    theme,
    currentTheme,
    isDarkMode,
    toggleTheme,
    setTheme: setSpecificTheme,
    themes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

export default ThemeContext;